import { useDraggable } from "@dnd-kit/core";
import { useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { CanvasElementView } from "./CanvasElement";
import type { CanvasElement } from "../types";
import { GRID_UNIT_PX, pixelDeltaToGridUnits } from "../gridMath";

interface Props {
  element: CanvasElement;
  selected: boolean;
  selectedElementId?: string | null;
  onSelect: (id: string) => void;
  onResize: (id: string, w: number, h: number) => void;
  fieldLabels?: Record<string, string>;
  tableSchemas?: Record<string, Array<{ key: string; label: string }>>;
}

export function DraggableResizableElement({ element, selected, selectedElementId, onSelect, onResize, fieldLabels, tableSchemas }: Props) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: element.id,
  });

  const resizeStateRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  const style: CSSProperties = {
    position: "absolute",
    left: element.x * GRID_UNIT_PX + (transform?.x ?? 0),
    top: element.y * GRID_UNIT_PX + (transform?.y ?? 0),
    width: element.w * GRID_UNIT_PX,
    height: element.h * GRID_UNIT_PX,
    direction: "ltr",
    cursor: isDragging ? "grabbing" : "grab",
    zIndex: selected ? 20 : isDragging ? 50 : 1,
    touchAction: "none",
    userSelect: "none",
  };

  const handlePointerDown = (e: ReactPointerEvent) => {
    if ((e.nativeEvent as any)._selectionHandled) return;
    (e.nativeEvent as any)._selectionHandled = true;
    onSelect(element.id);
  };

  function handleResizePointerDown(e: ReactPointerEvent) {
    e.stopPropagation();
    e.preventDefault();
    const target = e.target as Element & { setPointerCapture?: (id: number) => void };
    target.setPointerCapture?.(e.pointerId);

    resizeStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: element.w,
      startH: element.h,
    };

    function handleMove(moveEvent: PointerEvent) {
      const state = resizeStateRef.current;
      if (!state) return;
      const deltaWUnits = pixelDeltaToGridUnits(moveEvent.clientX - state.startX);
      const deltaHUnits = pixelDeltaToGridUnits(moveEvent.clientY - state.startY);
      const nextW = Math.max(1, state.startW + deltaWUnits);
      const nextH = Math.max(1, state.startH + deltaHUnits);
      onResize(element.id, nextW, nextH);
    }

    function handleUp() {
      resizeStateRef.current = null;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    }

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onPointerDown={(e) => {
        if (!(e.nativeEvent as any)._dragHandled) {
          (e.nativeEvent as any)._dragHandled = true;
          listeners?.onPointerDown?.(e as any);
        }
        handlePointerDown(e);
      }}
      onClick={(e) => {
        if (!(e.nativeEvent as any)._clickHandled) {
          (e.nativeEvent as any)._clickHandled = true;
          onSelect(element.id);
        }
      }}
    >
      <div style={{ width: "100%", height: "100%" }}>
        <CanvasElementView
          element={element}
          selected={selected}
          selectedElementId={selectedElementId}
          onSelect={onSelect}
          onResize={onResize}
          standalone={false}
          fieldLabels={fieldLabels}
          tableSchemas={tableSchemas}
          renderChild={(child) => (
            <div key={child.id} style={{ pointerEvents: "auto" }}>
              <DraggableResizableElement
                element={child}
                selected={child.id === selectedElementId}
                selectedElementId={selectedElementId}
                onSelect={onSelect}
                onResize={onResize}
                fieldLabels={fieldLabels}
                tableSchemas={tableSchemas}
              />
            </div>
          )}
        />
      </div>
      {selected && (
        <div
          data-testid={`resize-handle-${element.id}`}
          onPointerDown={handleResizePointerDown}
          style={{
            position: "absolute",
            right: -5,
            bottom: -5,
            width: 10,
            height: 10,
            background: "var(--designer-accent, #2563eb)",
            cursor: "nwse-resize",
            borderRadius: 2,
            zIndex: 30,
            pointerEvents: "auto",
          }}
        />
      )}
    </div>
  );
}
